import React, { useRef, useState, useEffect } from 'react';
import { useResponsive } from '../../hooks';

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actionButton?: {
    text: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  };
  actionButtons?: Array<{
    text: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    isMain?: boolean;
    render?: () => React.ReactNode;
  }>;
  closeOnClickOutside?: boolean;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Standardized modal component for detail views across the application
 * Based on the mobile-friendly pattern of the TransactionDetailModal
 */
const DetailModal: React.FC<DetailModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  actionButton,
  actionButtons = [],
  closeOnClickOutside = true,
  size = 'medium'
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnClickOutside]);
  
  // Mobile swipe down to close gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchStart(e.targetTouches[0].clientY);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchEnd(e.targetTouches[0].clientY);
  };
  
  const handleTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > 100;
    if (isDownSwipe && closeOnClickOutside) {
      onClose();
    }
    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!isOpen) return null;
  
  // Get action button class based on variant
  const getActionButtonClass = (variant?: 'primary' | 'secondary' | 'danger') => {
    switch (variant) {
      case 'secondary':
        return 'bg-primary-silver hover:bg-primary-silver/90 text-primary-charcoal';
      case 'danger':
        return 'bg-red-600 hover:bg-red-700';
      default:
        return 'bg-primary-charcoal hover:bg-primary-charcoal/90';
    }
  };

  // Get modal width based on size prop
  const getModalWidth = () => {
    switch (size) {
      case 'small':
        return 'max-w-lg';
      case 'large':
        return 'max-w-4xl';
      case 'medium':
      default:
        return 'max-w-3xl';
    }
  };

  // Combine legacy actionButton with new actionButtons array
  const allButtons = actionButton 
    ? [...actionButtons, { ...actionButton, isMain: true }]
    : actionButtons;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn" 
      onClick={(e) => {
        // This should only handle clicks directly on the backdrop
        const isBackdropClick = e.target === e.currentTarget;
        
        if (isBackdropClick && closeOnClickOutside) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div 
        ref={modalRef} 
        className={`bg-white shadow-xl ${isMobile 
          ? 'fixed inset-x-0 bottom-0 rounded-b-none rounded-t-xl max-h-[90vh] overflow-y-auto animate-slideUp' 
          : `w-full ${getModalWidth()} rounded-lg animate-slideIn flex flex-col`
        }`}
        style={isMobile ? {} : { maxHeight: 'calc(90vh)', height: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {/* Mobile swipe indicator */}
        {isMobile && (
          <div className="w-12 h-1.5 bg-primary-silver/50 rounded-full mx-auto mt-3 mb-1"></div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center border-b border-primary-silver/30 px-6 py-4 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-primary-charcoal">{title}</h2>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
            className="text-primary-charcoal/70 hover:text-primary-charcoal focus:outline-none"
            aria-label="Kapat"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className={`${isMobile ? 'p-6 overflow-y-auto' : 'p-6 overflow-y-auto flex-grow'}`}>
          {children}
        </div>
        
        {/* Footer with action buttons */}
        {allButtons.length > 0 && (
          <div className={`
            bg-white border-t border-primary-silver/30 p-4 sticky bottom-0 left-0 right-0
            ${isMobile ? 'flex flex-col gap-2' : 'flex flex-row-reverse gap-3 items-center'}
          `}>
            {isMobile ? (
              // Mobile layout - stacked buttons
              allButtons.map((btn, idx) => (
                btn.render ? (
                  <React.Fragment key={idx}>
                    {btn.render()}
                  </React.Fragment>
                ) : (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      btn.onClick();
                    }}
                    className={`w-full px-4 py-3 text-base font-medium ${
                      btn.variant !== 'secondary'
                        ? 'text-accent-white'
                        : 'text-primary-charcoal'
                    } border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal ${getActionButtonClass(btn.variant)}`}
                  >
                    {btn.text}
                  </button>
                )
              ))
            ) : (
              // Desktop layout - side by side buttons
              <>
                {allButtons.map((btn, idx) => (
                  btn.render ? (
                    <React.Fragment key={idx}>
                      {btn.render()}
                    </React.Fragment>
                  ) : (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        btn.onClick();
                      }}
                      className={`px-4 py-2 text-sm font-medium ${
                        btn.variant !== 'secondary'
                          ? 'text-accent-white border-transparent'
                          : 'text-primary-charcoal bg-white border-primary-silver hover:bg-primary-silver/10'
                      } border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal ${
                        btn.variant !== 'secondary' ? getActionButtonClass(btn.variant) : ''
                      }`}
                    >
                      {btn.text}
                    </button>
                  )
                ))}
                {!allButtons.some(btn => btn.text === 'Kapat') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                    className="mr-auto px-4 py-2 text-sm font-medium text-primary-charcoal bg-white border border-primary-silver rounded-md shadow-sm hover:bg-primary-silver/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
                  >
                    Kapat
                  </button>
                )}
              </>
            )}
          </div>
        )}
        
        {/* Show close button in footer when no action buttons */}
        {allButtons.length === 0 && (
          <div className={`
            bg-white border-t border-primary-silver/30 p-4 sticky bottom-0 left-0 right-0
            ${isMobile ? '' : 'flex justify-end'}
          `}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`
                ${isMobile ? 'w-full' : ''} px-4 py-2 text-sm font-medium
                ${isMobile ? 'text-accent-white bg-primary-charcoal' : 'text-primary-charcoal bg-white border-primary-silver'} 
                border rounded-md shadow-sm
                ${isMobile ? 'hover:bg-primary-charcoal/90' : 'hover:bg-primary-silver/10'} 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal
              `}
            >
              Kapat
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailModal; 